import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleMap, Polyline, LoadScript, Marker } from '@react-google-maps/api'; // Import Marker here
import Logger from './Logger';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LinearProgress } from '@mui/material'; // Progress bar
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // FontAwesome icons
import { faMapMarkerAlt, faSignInAlt, faSignOutAlt } from '@fortawesome/free-solid-svg-icons'; // Icons for halts and events
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const containerStyle = {
  width: '95%',
  height: '100vh',
};

const timelineStyle = {
  width: '25%',
  padding: '10px',
  borderRight: '1px solid #ccc',
  height: '100vh',
  overflowY: 'auto',
};

const eventContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginBottom: '30px',
};

const lineStyle = {
  width: '2px',
  height: '30px',
  backgroundColor: '#000',
  margin: '10px 0',
};

const timelineMainContainer = {
  display: 'flex',
  flexDirection: 'row',
  padding: '20px',
  width: '100%',
  justifyContent: 'space-between',
  backgroundColor: '#fff',
};

const eventRowStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

const initialCenter = {
  lat: 37.4219983,
  lng: -122.084,
};

const DriverLocationTracker = () => {
  const [startLocation, setStartLocation] = useState(null);
  const [significantLocations, setSignificantLocations] = useState([]);
  const [stopTimeline, setStopTimeline] = useState([]);
  const [onlineOfflineEvents, setOnlineOfflineEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeHaltLocation, setActiveHaltLocation] = useState(null); // State for active halt marker
  const [loading, setLoading] = useState(false); // Loading state for progress bar
  const [timelineLoading, setTimelineLoading] = useState(true);  // Loading for timeline
  const [mapLoading, setMapLoading] = useState(true);  // Loading for map
  const [mergedTimeline, setMergedTimeline] = useState([]);
  const [newEvents, setNewEvents] = useState([]);
  const location = useLocation();
  const mapInstanceRef = useRef(null);
  const userEmail = sessionStorage.getItem("userEmail");

  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  const getQueryParams = useCallback(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  // Function to handle timeline data loading
  useEffect(() => {
    if (mergedTimeline.length > 0) {
      setTimelineLoading(false);  // Set to false once the timeline data is loaded
    }
  }, [mergedTimeline]);

  // Combine loading states
  useEffect(() => {
    if (!timelineLoading && !mapLoading) {
      setLoading(false);  // Set to false only when both timeline and map are loaded
    } else {
      setLoading(true);   // Keep it true until both are loaded
    }
  }, [timelineLoading, mapLoading]);

  const fetchAddressFromLatLng = useCallback(async (lat, lng) => {
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyASvHxca4NGb5ObLmhETGUOmfWDMpcdmUg`;

    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        return data.results[0].formatted_address;
      } else {
        return "Address not available";
      }
    } catch (error) {
      logger.log(`Error fetching address: ${error.message}`, 'ERROR');
      return "Address not available";
    }
  }, [logger]);

  const calculateHalts = useCallback(async (locations) => {
    let haltStart = null;
    let haltLocation = null;
    const halts = [];

    for (let i = 1; i < locations.length; i++) {
      const distance = calculateDistance(locations[i - 1], locations[i]);

      if (distance <= 10) {
        if (!haltStart) {
          haltStart = new Date(locations[i - 1].timestamp);
          haltLocation = locations[i - 1];
        }
      } else {
        if (haltStart) {
          const haltEnd = new Date(locations[i].timestamp);
          const duration = Math.round((haltEnd - haltStart) / 60000); // in minutes
          if (duration > 0) {
            const address = await fetchAddressFromLatLng(haltLocation.lat, haltLocation.lng); // Fetch the address
            halts.push({
              location: haltLocation,
              address, // Store the fetched address
              duration,
              startTime: haltStart,
              endTime: haltEnd,
            });
          }
          haltStart = null;
        }
      }
    }

    if (haltStart) {
      const haltEnd = new Date(locations[locations.length - 1].timestamp);
      const duration = Math.round((haltEnd - haltStart) / 60000);
      const address = await fetchAddressFromLatLng(haltLocation.lat, haltLocation.lng); // Fetch the address
      halts.push({
        location: haltLocation,
        address, // Store the fetched address
        duration,
        startTime: haltStart,
        endTime: haltEnd,
      });
    }

    setStopTimeline(halts);
  }, [fetchAddressFromLatLng]);

  const fetchDriverLocations = useCallback(async () => {
    const queryParams = getQueryParams();
    const driverId = queryParams.get('driverId');

    setLoading(true); // Start loading

    try {
      const response = await fetch(`/api/ordera/location/${driverId}/${userEmail}`);

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const data = await response.json();
      const driverData = data;

      const selectedDateString = selectedDate.toISOString().split('T')[0];

      if (driverData.startLocation) {
        const startLatLng = driverData.startLocation.split(',').map(parseFloat);
        setStartLocation({ lat: startLatLng[0], lng: startLatLng[1] });
      }

      if (driverData.significantLocationLogs.length > 0) {
        const filteredLocations = driverData.significantLocationLogs
          .filter(log => log.timestamp.startsWith(selectedDateString))
          .map(log => ({
            lat: parseFloat(log.tempLocation.split(',')[0]),
            lng: parseFloat(log.tempLocation.split(',')[1]),
            timestamp: log.timestamp,
          }));
        setSignificantLocations(filteredLocations);
        calculateHalts(filteredLocations);
      }

      const filteredOnlineOfflineEvents = [
        ...driverData.onlineEvents.filter(event => event.timestamp.startsWith(selectedDateString)).map(event => ({ ...event, status: 'Online' })),
        ...driverData.offlineEvents.filter(event => event.timestamp.startsWith(selectedDateString)).map(event => ({ ...event, status: 'Offline' })),
      ];
      setOnlineOfflineEvents(filteredOnlineOfflineEvents);

      const filteredNewEvents = driverData.events
        .filter(event => event.timeStamp.startsWith(selectedDateString))
        .map(event => ({
          type: 'event',
          timestamp: new Date(new Date(event.timeStamp).getTime() - (5.5 * 60 * 60 * 1000)),
          data: event,
        }));

      // Store the new events
      setNewEvents(filteredNewEvents);

    } catch (error) {
      logger.log(`Error fetching data: ${error.message}`, 'ERROR');
    } finally {
      setLoading(false); // Stop loading
    }
  }, [getQueryParams, userEmail, selectedDate, logger, calculateHalts]);

  const calculateDistance = (location1, location2) => {
    const toRad = (value) => value * Math.PI / 180;
    const R = 6371e3; // Radius of Earth in meters
    const lat1 = toRad(location1.lat);
    const lat2 = toRad(location2.lat);
    const deltaLat = toRad(location2.lat - location1.lat);
    const deltaLng = toRad(location2.lng - location1.lng);

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const mergeAndSortTimeline = useCallback(() => {
    const stopsWithTimestamps = stopTimeline.map(stop => ({
      type: 'stop',
      timestamp: stop.startTime,
      data: stop,
    }));

    const eventsWithTimestamps = onlineOfflineEvents.map(event => ({
      type: 'event',
      // Convert the timestamp to 'en-US' locale with UTC timezone formatting
      timestamp: new Date(new Date(event.timestamp).getTime() - (5.5 * 60 * 60 * 1000)),
      data: event,
    }));

    const merged = [...stopsWithTimestamps, ...eventsWithTimestamps, ...newEvents].sort((a, b) => a.timestamp - b.timestamp);
    setMergedTimeline(merged);
  }, [stopTimeline, onlineOfflineEvents, newEvents]);

  useEffect(() => {
    fetchDriverLocations();
  }, [fetchDriverLocations]);

  useEffect(() => {
    mergeAndSortTimeline();
  }, [mergeAndSortTimeline]);

  const handleHaltClick = (location) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(location);
      mapInstanceRef.current.setZoom(15); // Pan and zoom when clicking on halt location
      setActiveHaltLocation(location); // Set the active halt location
    }
  };

  const handleMapLoad = (map) => {
    mapInstanceRef.current = map; // Assign map instance to the ref
    setMapLoading(false);
  };

  return (
    <div style={timelineMainContainer}>
      {/* Timeline Section */}
      <div style={timelineStyle}>
        <h3>Select Date</h3>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setTimelineLoading(true);
          }}
          dateFormat="yyyy-MM-dd"
        />

        <h3>Timeline</h3>
        {loading ? (
          <LinearProgress /> // Progress bar while data is loading
        ) : (
          <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
            {mergedTimeline
              .filter((entry) => entry.type !== 'stop' || entry.data.duration > 0)
              .map((entry, index) => (
                <li key={index} style={eventContainerStyle}>
                  <div style={eventRowStyle}>
                    {/* Symbol for each event */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {entry.type === 'stop' ? (
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" style={{ color: '#07a6ef' }} />
                      ) : (
                        <FontAwesomeIcon
                          icon={entry.data.status === 'Online' ? faSignInAlt : faSignOutAlt}
                          size="2x"
                          style={{ color: entry.data.status === 'Online' ? 'green' : 'red' }}
                        />
                      )}
                    </div>

                    {/* Event details */}
                    <div
                      onClick={() =>
                        entry.type === 'stop' ? handleHaltClick(entry.data.location) : null
                      }
                      style={{
                        cursor: entry.type === 'stop' ? 'pointer' : 'default',
                        marginLeft: '10px',
                      }}
                    >
                      {entry.type === 'stop' ? (
                        <div>
                          {/* Start time in green and duration in blue */}
                          <span style={{ color: 'green' }}>
                            Start Time: {entry.data.startTime.toLocaleTimeString()}
                          </span>{' '}
                          <span style={{ color: '#07a6ef', marginLeft: '10px' }}>
                            Halt Time: {entry.data.duration} mins
                          </span>
                          <br />
                          {/* Halt at {entry.data.address} */}
                        </div>
                      ) : entry.data.eventType ? (
                        <div>
                          {entry.data.eventType} - {entry.data.reason} at {entry.timestamp.toLocaleTimeString()}
                        </div>
                      ) : (
                        <div>
                          {entry.data.status} at{' '}
                          {new Date(new Date(entry.data.timestamp).getTime() - 5.5 * 60 * 60 * 1000).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Line between events and Address beside it */}
                  {index <= mergedTimeline.length - 1 && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={lineStyle}></div>
                      <div style={{ marginLeft: '10px', color: '#555', fontStyle: 'italic' }}>
                        {entry.data.address}
                      </div>
                    </div>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>

      <div style={containerStyle}>
        <LoadScript googleMapsApiKey="AIzaSyASvHxca4NGb5ObLmhETGUOmfWDMpcdmUg">
          <GoogleMap
            onLoad={handleMapLoad}
            mapContainerStyle={containerStyle}
            center={startLocation || initialCenter}
            zoom={15}
          >

            {startLocation && (
              <Marker position={startLocation} label="Start" />
            )}

            {activeHaltLocation && (
              <Marker position={activeHaltLocation}
                zoom={80} />
            )}

            {significantLocations.length > 1 && (
              <Polyline
                path={significantLocations.map(loc => ({
                  lat: loc.lat,
                  lng: loc.lng,
                }))}
                options={{
                  strokeColor: "#007bff",
                  strokeOpacity: 1.0,
                  strokeWeight: 4,
                }}
              />
            )}

          </GoogleMap>

        </LoadScript>
      </div>
    </div>
  );
};

export default DriverLocationTracker;
